import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsStorage } from '@next/core-lib/settings';
import { UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  skipWhile,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import { FeedAdviceUpdateValidResponse } from 'src/app/cycle/models/flock-registration-service.model';
import { FlockRegistrationService } from 'src/app/cycle/services/flock-registration.service';
import { ProductService } from 'src/app/product/services/product.service';

import { ReferenceScheme } from 'src/app/reference-scheme/models/reference-scheme.model';
import { ReferenceSchemeService } from 'src/app/reference-scheme/services/reference-scheme.service';
import { PoultryContextKeys } from 'src/app/shared/constants';
import { Product } from 'src/app/shared/models/shared.model';

import { SettingsServiceHelper } from 'src/app/shared/services/settingsHelperService';

import * as feedAdviceActions from './feed-advice.actions';
import * as productActions from '../../product/store/actions/product.actions';
import {
  FeedAdvice,
  FeedAdviceModuleState,
  UpsertFeedAdviceResponse,
} from '../models/feed-advice.models';
import { FeedAdviceService } from '../services/feed-advice.service';

@Injectable()
export class FeedAdviceEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<FeedAdviceModuleState>>(Store);
  private router = inject(Router);
  private dialogService = inject(DialogService);
  private readonly locale = inject<LocaleService>(LocaleService);
  private feedAdviceService = inject(FeedAdviceService);
  private productService = inject(ProductService);
  private flockRegistrationService = inject(FlockRegistrationService);
  private referenceSchemeService = inject(ReferenceSchemeService);
  private settingsService = inject(SettingsServiceHelper);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  /* LOAD DATA EFFECTS */
  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadData),
      concatMap(() =>
        of(
          feedAdviceActions.clearFeedAdviceState(),
          productActions.loadProductsRequest(),
          feedAdviceActions.loadFeedAdvicesRequest(),
        ),
      ),
    ),
  );

  loadFeedAdvicesRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadFeedAdvicesRequest),
      mergeMap(() =>
        this.feedAdviceService.getFeedAdvices().pipe(
          switchMap((feedAdvices: FeedAdvice[]) =>
            of(
              feedAdviceActions.loadFeedAdvicesSuccess({ feedAdvices }),
              feedAdviceActions.loadProductsByCountryRequest(),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.loadFeedAdvicesFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadProductsByCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadProductsByCountryRequest),
      switchMap(() =>
        combineLatest([this.store.select(UserSelectors.selectLocation)]).pipe(
          skipWhile((selectedLocation) => selectedLocation[0] === undefined),
          take(1),
        ),
      ),
      exhaustMap((selectedLocation) =>
        this.productService.getProducts().pipe(
          switchMap((products) =>
            of(
              feedAdviceActions.loadProductsByCountrySuccess({
                products: (products as Product[]).filter(
                  (x) => x?.country === selectedLocation[0]?.country.toString(),
                ),
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.loadProductsByCountryFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadReferenceSchemesByCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadReferenceSchemesByCountryRequest),
      switchMap(() =>
        combineLatest([this.store.select(UserSelectors.selectLocation)]).pipe(
          skipWhile((selectedLocation) => selectedLocation[0] === undefined),
          take(1),
        ),
      ),
      exhaustMap((selectedLocation) =>
        this.referenceSchemeService.getReferenceSchemes(selectedLocation[0]?.country).pipe(
          switchMap((referenceSchemes: ReferenceScheme[]) =>
            of(
              feedAdviceActions.loadReferenceSchemesByCountrySuccess({
                referenceSchemes,
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.loadProductsByCountryFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  setBaseFeedAdviceRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.setBaseFeedAdviceRequest),
      exhaustMap((baseFeedAdvice) => {
        this.settingsService.setSettings(
          PoultryContextKeys.REFERENCE_FEED_ADVICE,
          baseFeedAdvice.feedAdvice,
          SettingsStorage.AppLocal,
        );
        return of(feedAdviceActions.setBaseFeedAdviceSuccess(baseFeedAdvice));
      }),
    ),
  );

  /* NAVIGATION EFFECTS */
  createFromReferenceAdviceNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.createFromReferenceAdviceNavigation),
        exhaustMap(() => of(feedAdviceActions.loadReferenceSchemesByCountryRequest)),
        tap(() => {
          this.router.navigate(['feed-advice', 'create-from-reference']);
        }),
      ),
    {
      dispatch: false,
    },
  );

  createAdviceNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.createAdviceNavigation),
        tap(() => {
          this.router.navigate(['feed-advice', 'create']);
        }),
      ),
    {
      dispatch: false,
    },
  );

  editAdviceNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.editAdviceNavigation),
        tap((action) => {
          this.router.navigate(['feed-advice', 'edit', action.feedAdvice.id]);
        }),
      ),
    {
      dispatch: false,
    },
  );

  viewAdviceNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.viewAdviceNavigation),
        tap((action) => {
          this.router.navigate(['feed-advice', 'view', action.feedAdvice.id]);
        }),
      ),
    {
      dispatch: false,
    },
  );

  /* UPSERT EFFECTS */
  upsertFeedAdviceRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.upsertFeedAdviceRequest),
      exhaustMap(({ feedAdvice }) =>
        this.feedAdviceService.upsertFeedAdvice(feedAdvice).pipe(
          map((response: UpsertFeedAdviceResponse) =>
            feedAdviceActions.upsertFeedAdviceSuccess({
              feedAdvice: {
                ...feedAdvice,
                id: response.id,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.upsertFeedAdviceFailed({ error }),
              feedAdviceActions.loadFeedAdvicesRequest(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
  upsertFeedAdviceSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.upsertFeedAdviceSuccess),
        tap((_) => {
          this.router.navigate(['feed-advice']);
        }),
      ),
    {
      dispatch: false,
    },
  );

  /* DELETE EFFECTS */
  deleteAdviceConfirm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.deleteAdviceConfirm),
      exhaustMap(({ feedAdvice }) =>
        combineLatest([
          this.flockRegistrationService.checkUpdateValidityFeedAdvice(feedAdvice),
          of(feedAdvice),
        ]).pipe(take(1)),
      ),
      switchMap(([response, feedAdvice]) =>
        this.dialogService
          .confirmWarning({
            text: (response as FeedAdviceUpdateValidResponse).isUsed
              ? this.locale.translate(`feed-advice.dialog.deleteConfirmWhenInUse`)
              : this.locale.translate(`feed-advice.dialog.deleteConfirm`),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  feedAdviceActions.deleteAdviceRequest({
                    feedAdviceId: feedAdvice.id,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteAdviceRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.deleteAdviceRequest),
      mergeMap((action) =>
        this.feedAdviceService.deleteFeedAdvice(action.feedAdviceId).pipe(
          map(() => feedAdviceActions.deleteAdviceSuccess({ feedAdviceId: action.feedAdviceId })),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.deleteAdviceFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
